/* eslint-disable complexity */
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import instance from '../config/axios';
import { useNavigate } from 'react-router-dom';
import Select, { components } from 'react-select';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import CustomDatePicker from '../components/CustomDatePicker';
import Loading from '../components/Loading';
import { genders, title, userType } from '../constant/constant';
import {
  PrescriptionProp,
  ReactSelectOptionProp,
  ReactSelectOptionPropTwo,
} from '../interface/interface';
import { DownCaretIcon, PrescriptionIcon } from '../assets/svgImages';
import { patientCreateValidator } from '../validator/patientCreateValidator';
import { validateEmail } from '../utils/validateEmail';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import showToast from '../components/ToastComponent';
import isValidAustralianPostcode from 'is-valid-australian-postcode';
import { getShippingAddress } from '../utils/formatAddress';

const OnboardPatient = () => {
  const { user } = useAuth();
  // const auth = useAuth();
  // const { nativeView } = useContext(UserContext);
  const navigate = useNavigate();

  const [data, setData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    middleName: '',
    preferredName: '',
    email: '',
    dob: undefined,
    mobile: '',
    home: '',
    gender: '',
    medicare: '',
    medicare_reference: '',
    medicareExpiry: undefined,
    dva: '',
    contactName: '',
    contactPhone: '',
    contactRelationship: '',
    internal: { label: 'Internal', value: true },
    prescriptions: [] as PrescriptionProp[],
  });

  const [error, setError] = useState<boolean>(false);

  const postal = useRef<HTMLInputElement | null>(null);

  const [doctors, setDoctors] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectTitle, setSelectTitle] = useState<null | {
    label: string;
    value: string;
  }>(null);

  const [gender, setGender] = useState<ReactSelectOptionProp>();

  const [shippingAddress1, setShippingAddress1] = useState<string>('');
  const [shippingAddress2, setShippingAddress2] = useState<string>('');
  const [shippingCity, setShippingCity] = useState<string>('');
  const [shippingProvince, setShippingProvince] = useState<string>('');
  const [shippingZip, setShippingZip] = useState<string>('');
  const [shippingCountry, setShippingCountry] = useState<string>('Australia');

  const [emergencyConsent, setEmergencyConsent] = useState<boolean>(false);

  const [, setAddressType] = useState<null | ReactSelectOptionProp>({
    label: 'Residential',
    value: 'residential',
  });

  // const session = window.sessionStorage.getItem(
  //   `oidc.user:${process.env.AUTHENTIK_HOST}/application/o/${nativeView ? `mvr-mobile:${process.env.MOBILE_AUTHENTIK_CLIENT_ID}` : `mvr:${process.env.AUTHENTIK_CLIENT_ID}`}`,
  // );

  // if (!session) {
  //   console.log('Session Timeout Sign out the user');
  //   auth.signoutRedirect();
  // }

  useEffect(() => {
    if (postal.current) {
      postal.current.addEventListener('wheel', (e) => e.preventDefault());
    }
  }, []);

  const handleInput = (
    e: ChangeEvent<HTMLInputElement>,
    id?: number | undefined,
  ) => {
    if (!id) {
      setData({
        ...data,
        [e.target.name]: e.target.value,
      });
    }

    const selectedPres = data.prescriptions.find((x) => x.id === id);
    if (!selectedPres) return;

    selectedPres[e.target.name] = e.target.value;

    setData({
      ...data,
    });
  };

  const handleDate = (date: Date, key: string, id?: number | undefined) => {
    if (!id) {
      setData({
        ...data,
        [key]: new Date(date),
      });
    }
    const selectedPres = data.prescriptions.find((x) => x.id === id);
    if (!selectedPres) return;

    selectedPres.created_date = date;

    setData({
      ...data,
    });
  };

  const clearInput = () => {
    setData({
      title: '',
      firstName: '',
      lastName: '',
      middleName: '',
      preferredName: '',
      email: '',
      dob: undefined,
      mobile: '',
      home: '',
      gender: '',
      medicare: '',
      medicare_reference: '',
      medicareExpiry: undefined,
      dva: '',
      contactName: '',
      contactPhone: '',
      contactRelationship: '',
      prescriptions: [],
      internal: { label: 'Internal', value: true },
    });
    setAddressType(null);
    setGender(undefined);
    setSelectTitle(null);
  };

  const addPrescription = () => {
    const newPrescription = [];

    newPrescription.push({
      id: data.prescriptions?.length + 1,
      created_date: moment().toDate(),
      quantity: '',
      repeats: '',
      interval: '',
      eRxToken: '',
      product: { label: '', value: 0 },
      doctor: { label: '', value: 0 },
      dosage: '',
    });

    data.prescriptions.push(...newPrescription);

    setData({
      ...data,
      prescriptions: data.prescriptions,
    });
  };

  const removePrescription = (id: number) => {
    const afterRemovedPrescriptions = data.prescriptions.filter(
      (x) => x.id !== id,
    );

    const newPrescriptionList = [];

    for (let i = 0; i < afterRemovedPrescriptions.length; i++) {
      newPrescriptionList.push({
        id: id + 1,
        created_date: afterRemovedPrescriptions[i].created_date,
        quantity: afterRemovedPrescriptions[i].quantity,
        repeats: afterRemovedPrescriptions[i].repeats,
        interval: afterRemovedPrescriptions[i].interval,
        eRxToken: afterRemovedPrescriptions[i].eRxToken,
        product: afterRemovedPrescriptions[i].product,
        doctor: afterRemovedPrescriptions[i].doctor,
        dosage: afterRemovedPrescriptions[i].dosage,
      });
    }

    setData({
      ...data,
      prescriptions: newPrescriptionList,
    });
  };

  const handleSelect = (
    selectedOption: { label: string; value: string | number },
    name: string,
    id?: number | undefined,
  ) => {
    const selectedPres = data.prescriptions.find((x) => x.id === id);
    if (!selectedPres) return;

    selectedPres[name] = selectedOption;

    setData({
      ...data,
    });
  };

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    try {
      e.preventDefault();

      const validateZipCode = isValidAustralianPostcode(shippingZip);
      if (!validateZipCode) {
        showToast({
          message: 'Please Enter Valid Postcode',
          type: 'warning',
        });
        return;
      }

      const frameData = {
        userData: {
          title: data.title,
          firstName: data.firstName,
          lastName: data.lastName,
          middleName: data.middleName,
          preferredName: data.preferredName,
          gender: data.gender,
          dob: data.dob
            ? moment(data.dob).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z'
            : '-',
          email: data.email,
          mobile: data.mobile, //Primary Contact
          home: data.home, // seconday Contact
          medicare: data.medicare,
          medicare_reference: data.medicare_reference, // IRN
          medicareExpiry: data.medicareExpiry
            ? moment(data.medicareExpiry).format('YYYY-MM-DDTHH:mm:ss.SSS') +
              'Z'
            : '-',
          dva: data.dva,
          internal: data.internal.value,
        },
        address: {
          address_line_one: shippingAddress1,
          address_line_two: shippingAddress2,
          city: shippingCity,
          state: shippingProvince,
          post_code: shippingZip,
          street_number: '',
          street_name: '',
        },
        emergency_contact_information: {
          contact_name: data.contactName,
          contact_number: data.contactPhone,
          contact_relationship: data.contactRelationship,
          consent: emergencyConsent,
        },
        prescriptions: data.prescriptions,
      };

      const patientDetailValidate = patientCreateValidator(frameData);

      if (patientDetailValidate) {
        console.log(patientDetailValidate);
        setError(true);
        return;
      }

      const response = await instance.post(
        '/api/v1/patient/create',
        frameData,
        {
          headers: {
            Authorization: `Bearer ${user?.access_token}`,
          },
        },
      );

      if (response.status === 201) {
        showToast({
          message: 'Patient Created Successfully',
          type: 'success',
        });
        navigate('/patients');
      } else {
        showToast({
          message: response.data.message,
          type: 'warning',
        });
      }
    } catch (err) {
      console.log(err);
      showToast({
        message: 'Error creating the Patient',
        type: 'error',
      });
    }
  };

  const fetchDoctorAndProducts = async () => {
    try {
      const response = await instance.get('/api/v1/doctor-products', {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      });

      setDoctors(
        response.data.doctors.map((doc: { name: string; id: number }) => ({
          label: doc.name,
          value: doc.id,
        })),
      );
      setProducts(
        response.data.products.map((prd: { name: string; id: number }) => ({
          label: prd.name,
          value: prd.id,
        })),
      );
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const { isFetching: fetchDocProductLoad } = useQuery({
    queryKey: ['doctor-products'],
    queryFn: fetchDoctorAndProducts,
  });

  if (fetchDocProductLoad) {
    return <Loading />;
  }

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <DownCaretIcon />
      </components.DropdownIndicator>
    );
  };

  return (
    <>
      <div className="space-y-10 divide-y divide-gray-900/10 add-user">
        <div>
          <div className="px-4 sm:px-0">
            <h2 className="mb-5 text-2xl font-bold text-primary-blue-500">
              Add Patient Profile
            </h2>
          </div>

          <div>
            <div>
              <div className="p-7 bg-secondary-pink-50 shadow rounded-xl mb-10">
                <h3 className="mb-3 text-xl font-semibold text-primary-blue-500">
                  Personal Information
                </h3>
                <div className="bg-white rounded px-10 py-8 rounded-md">
                  <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-7">
                    <div className="sm:col-span-1">
                      <label className="text-lg font-semibold  text-secondary-pink-700">
                        Title
                      </label>
                      <div className="">
                        <Select
                          value={selectTitle}
                          options={title as ReactSelectOptionProp[]}
                          onChange={(selectedOption) => {
                            setSelectTitle(selectedOption);
                            setData({
                              ...data,
                              title: selectedOption!.value,
                            });
                          }}
                          isSearchable={false}
                          components={{
                            DropdownIndicator,
                            IndicatorSeparator: () => null,
                          }}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="first-name"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        First name <span className="text-red-500">*</span>
                      </label>
                      <div className="">
                        <input
                          type="text"
                          name="firstName"
                          id="first-name"
                          autoComplete="given-name"
                          className={`text-sm font-regular border  
                        rounded-md p-2 w-full 
                        ${
                          error && !data.firstName
                            ? 'border-red-400'
                            : 'border-secondary-cornflowerblue-400'
                        }`}
                          value={data.firstName}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                      {error && !data.firstName && (
                        <small className="text-red-600">
                          First Name is required
                        </small>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="last-name"
                        className="text-lg font-semibold text-secondary-pink-700"
                      >
                        Last name <span className="text-red-500">*</span>
                      </label>
                      <div className="">
                        <input
                          type="text"
                          name="lastName"
                          id="last-name"
                          autoComplete="family-name"
                          className={`text-sm font-regular border rounded-md p-2 w-full ${
                            error && !data.lastName
                              ? 'border-red-400'
                              : 'border-secondary-cornflowerblue-400'
                          }`}
                          value={data.lastName}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                      {error && !data.lastName && (
                        <small className="text-red-600">
                          Last Name is required
                        </small>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="middle-name"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Middle name
                      </label>
                      <div className="">
                        <input
                          type="text"
                          name="middleName"
                          id="middle-name"
                          autoComplete="given-name"
                          className="text-sm font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                          value={data.middleName}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-6 mt-5">
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="prefer-name"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Preferred name
                      </label>
                      <div className="">
                        <input
                          type="text"
                          name="preferredName"
                          id="prefer-name"
                          autoComplete="prefer-name"
                          className="text-sm font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                          value={data.preferredName}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-lg font-semibold  text-secondary-pink-700">
                        Gender <span className="text-red-500">*</span>
                      </label>
                      <div className="">
                        <Select
                          value={gender}
                          options={genders as ReactSelectOptionProp[]}
                          onChange={(selectedOption) => {
                            setGender(selectedOption as ReactSelectOptionProp);
                            setData({
                              ...data,
                              gender: selectedOption!.value,
                            });
                          }}
                          isSearchable={false}
                          components={{
                            DropdownIndicator,
                            IndicatorSeparator: () => null,
                          }}
                          className={
                            error && !data.gender ? 'select-error' : ''
                          }
                        />
                      </div>
                      {error && !data.gender && (
                        <small className="text-red-600">
                          Gender is required
                        </small>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-lg font-semibold  text-secondary-pink-700">
                        Date Of Birth <span className="text-red-500">*</span>
                      </label>
                      <div className="">
                        <div className="relative custom-date-picker">
                          <CustomDatePicker
                            value={data.dob}
                            handleDate={(date: Date) => handleDate(date, 'dob')}
                            className={error && !data.dob ? 'dob-error' : ''}
                            maxDate
                          />
                        </div>
                      </div>
                      {error && !data.dob && (
                        <small className="text-red-600">DOB is required</small>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-6 mt-5">
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="email"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Email address <span className="text-red-500">*</span>
                      </label>
                      <div className="">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          className={`text-sm font-regular border 
                        rounded-md p-2 w-full 
                        ${error && !data.email && !validateEmail(data.email) ? 'border-red-400' : 'border-secondary-cornflowerblue-400'}`}
                          onChange={(e) => handleInput(e)}
                          value={data.email}
                        />
                      </div>
                      {error && !data.email ? (
                        <small className="text-red-600">
                          Email is required
                        </small>
                      ) : error && !validateEmail(data.email) ? (
                        <small className="text-red-600">
                          Email Not in valid form
                        </small>
                      ) : (
                        ''
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="mobile"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Primary Contact <span className="text-red-500">*</span>
                      </label>
                      <div className=" flex rounded-md shadow-sm">
                        <span
                          className={`inline-flex items-center rounded-l-md border border-r-0 
                       px-3 text-gray-500 text-sm ${
                         (error && !data.mobile) ||
                         (error && data.mobile.split('').length < 9) ||
                         (error && data.mobile.split('').length > 9)
                           ? 'border-red-400'
                           : 'border-secondary-cornflowerblue-400'
                       }`}
                        >
                          +61
                        </span>
                        <input
                          type="number"
                          name="mobile"
                          id="mobile"
                          className={`rounded-none rounded-r-md placeholder:text-gray-400 text-sm 
                        font-regular border  rounded-md p-2 w-full ${
                          (error && !data.mobile) ||
                          (error && data.mobile.split('').length < 9) ||
                          (error && data.mobile.split('').length > 9)
                            ? 'border-red-400'
                            : 'border-secondary-cornflowerblue-400'
                        }`}
                          placeholder="123456789"
                          onChange={(e) => handleInput(e)}
                          value={data.mobile}
                        />
                      </div>
                      {error && !data.mobile ? (
                        <small className="text-red-600">
                          Contact number is required
                        </small>
                      ) : error && data.mobile.split('').length < 9 ? (
                        <small className="text-red-600">
                          Please enter valid contact number
                        </small>
                      ) : error &&
                        data.mobile &&
                        data.mobile.split('').length > 9 ? (
                        <small className="text-red-600">
                          Please enter valid contact number
                        </small>
                      ) : (
                        ''
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="home"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Secondary Contact
                      </label>
                      <div className=" flex rounded-md shadow-sm">
                        <span
                          className="inline-flex items-center rounded-l-md border border-r-0 
                      border-secondary-cornflowerblue-400 px-3 text-gray-500 sm:text-sm"
                        >
                          +61
                        </span>
                        <input
                          type="number"
                          name="home"
                          id="home"
                          className="rounded-none rounded-r-md placeholder:text-gray-400 text-sm 
                        font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                          placeholder="123456789"
                          onChange={(e) => handleInput(e)}
                          value={data.home}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-7 mt-5">
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="medicare"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Medicare No
                      </label>
                      <div className="">
                        <input
                          type="number"
                          name="medicare"
                          id="medicare"
                          autoComplete="address-level1"
                          className={`text-sm font-regular border  rounded-md p-2 w-full
                        
                        ${
                          error &&
                          data.medicare &&
                          data.medicare.split('').length < 10
                            ? 'border-red-400'
                            : error &&
                                data.medicare &&
                                data.medicare.split('').length > 10
                              ? 'border-red-400'
                              : error &&
                                  data.medicare &&
                                  !['2', '3', '4', '5', '6'].includes(
                                    data.medicare.split('')[0],
                                  )
                                ? 'border-red-400'
                                : error &&
                                    data.medicare &&
                                    data.medicare.charAt(
                                      data.medicare.length - 1,
                                    ) === '0'
                                  ? 'border-red-400'
                                  : 'border-secondary-cornflowerblue-400'
                        }
                        `}
                          value={data.medicare}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                      {error &&
                      data.medicare &&
                      data.medicare.split('').length < 10 ? (
                        <small className="text-red-600">
                          Medicare Must be 10 digit
                        </small>
                      ) : error &&
                        data.medicare &&
                        data.medicare.split('').length > 10 ? (
                        <small className="text-red-600">
                          Medicare Not to be more than 10 digits
                        </small>
                      ) : error &&
                        data.medicare &&
                        !['2', '3', '4', '5', '6'].includes(
                          data.medicare.split('')[0],
                        ) ? (
                        <small className="text-red-600">
                          First digit must be in the range of 2-6
                        </small>
                      ) : error &&
                        data.medicare &&
                        data.medicare.charAt(data.medicare.length - 1) ===
                          '0' ? (
                        <small className="text-red-600">
                          Last digit cannot be 0
                        </small>
                      ) : (
                        ''
                      )}
                    </div>

                    <div className="sm:col-span-1">
                      <label
                        htmlFor="medicare_reference"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        IRN
                      </label>
                      <div className="">
                        <input
                          type="number"
                          name="medicare_reference"
                          id="medicare_reference"
                          autoComplete="address-level1"
                          className="text-sm font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                          value={data.medicare_reference}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                      {error &&
                        data.medicare_reference &&
                        Number(data.medicare_reference) > 10 && (
                          <small className="text-red-600">
                            The Reference Number should be 1 digit
                          </small>
                        )}
                    </div>

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="medicare_expiry"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Expiry
                      </label>
                      <div className="">
                        <div className="relative">
                          <CustomDatePicker
                            value={data.medicareExpiry}
                            handleDate={(date: Date) =>
                              handleDate(date, 'medicareExpiry')
                            }
                            minDate={true}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label
                        htmlFor="dva"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        DVA
                      </label>
                      <div className="">
                        <input
                          type="number"
                          name="dva"
                          id="dva"
                          autoComplete="address-level1"
                          className="text-sm font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                          value={data.dva}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2 mt-2">
                      <label className="text-lg font-semibold  text-secondary-pink-700">
                        Patient Type <span className="text-red-500">*</span>
                      </label>
                      <div className="">
                        <Select
                          value={data.internal}
                          options={userType as ReactSelectOptionPropTwo[]}
                          onChange={(selectedOption) => {
                            setData({
                              ...data,
                              internal:
                                selectedOption as ReactSelectOptionPropTwo,
                            });
                          }}
                          isSearchable={false}
                          components={{
                            DropdownIndicator,
                            IndicatorSeparator: () => null,
                          }}
                          className={
                            error && !data.gender ? 'select-error' : ''
                          }
                        />
                      </div>
                      {error && !data.gender && (
                        <small className="text-red-600">
                          Gender is required
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-7 bg-secondary-pink-50 shadow rounded-xl mb-10">
                <h3 className="mb-3 text-xl font-semibold text-primary-blue-500">
                  Address Information{' '}
                  <small className="text-sm font-regular">(optional)</small>
                </h3>
                <div className="bg-white rounded px-10 py-8 rounded-md">
                  <div className="grid grid-cols-1 gap-x-8  register_patient">
                    <AddressAutocomplete
                      addressRefValue={shippingAddress1}
                      address1ManualSetter={(address1) =>
                        setShippingAddress1(address1)
                      }
                      state={{
                        city: shippingCity,
                        country: shippingCountry,
                        zip: shippingZip,
                        state: shippingProvince,
                        address2: shippingAddress2 ?? '',
                        streetNumber: '',
                        streetName: '',
                        unit: '',
                      }}
                      setter={(data) => {
                        setShippingCity(data.city);
                        setShippingCountry(data.country);
                        setShippingZip(data.zip);
                        setShippingProvince(data.state);
                        setShippingAddress2(data.address2 ?? '');
                        if (process.env.VITE_MAPS_AUTOCOMPLETE_KEY)
                          setShippingAddress1(
                            data?.streetName
                              ? getShippingAddress(data)
                              : shippingAddress1,
                          );
                      }}
                      freezeAddressFields={false}
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact Information */}
              <div className="p-7 bg-secondary-pink-50 shadow rounded-xl">
                <h3 className="mb-3 text-xl font-semibold text-primary-blue-500">
                  Emergency Contact Information{' '}
                  <small className="text-sm font-regular">(optional)</small>
                </h3>
                <div className="bg-white rounded  px-10 py-8 rounded-md">
                  <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-3">
                    <div className="sm:col-span-1">
                      <label
                        htmlFor="contact-name"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Emergency Contact Name
                      </label>
                      <div className="">
                        <input
                          type="text"
                          name="contactName"
                          id="contact-name"
                          autoComplete="given-name"
                          className={`text-sm font-regular border  
                        rounded-md p-2 w-full  border-secondary-cornflowerblue-400
                        `}
                          value={data.contactName}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-1">
                      <label
                        htmlFor="contact-phone"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Contact Phone
                      </label>
                      <div className=" flex rounded-md shadow-sm">
                        <span
                          className="inline-flex items-center rounded-l-md border border-r-0 
                      border-secondary-cornflowerblue-400 px-3 text-gray-500 text-sm"
                        >
                          +61
                        </span>
                        <input
                          type="number"
                          name="contactPhone"
                          id="conatact-phone"
                          className="rounded-none rounded-r-md placeholder:text-gray-400 text-sm 
                        font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                          placeholder="123456789"
                          onChange={(e) => handleInput(e)}
                          value={data.contactPhone}
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-1">
                      <label
                        htmlFor="contact-relationship"
                        className="text-lg font-semibold  text-secondary-pink-700"
                      >
                        Relationship to Patient
                      </label>
                      <div className="">
                        <input
                          type="text"
                          name="contactRelationship"
                          id="contact-relationship"
                          autoComplete="given-name"
                          className={`text-sm font-regular border  
                        rounded-md p-2 w-full  border-secondary-cornflowerblue-400
                        `}
                          value={data.contactRelationship}
                          onChange={(e) => handleInput(e)}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2 mt-3">
                      <input
                        id="comments"
                        aria-describedby="comments-description"
                        name="comments"
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        checked={emergencyConsent}
                        onChange={() => setEmergencyConsent(!emergencyConsent)}
                      />
                      <small className="ml-3 text-1xl text-primary-blue-500">
                        The patient consents for their Emergency Contact to
                        speak on their behalf
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prescription */}
              <button
                className="bg-primary-blue-500 rounded-md text-white text-base font-semibold px-8 py-3 w-full flex max-w-fit mt-5"
                onClick={() => addPrescription()}
              >
                <PrescriptionIcon />
                <span className="ml-3">Add Prescription</span>
              </button>

              {data.prescriptions.length > 0 &&
                data.prescriptions.map((pres) => (
                  <div className="relative" key={pres.id}>
                    <div
                      className="grid w-full grid-cols-1 gap-x-6 gap-y-8 grid-cols-6 bg-white p-5 mt-5 rounded-md shadow"
                      key={pres.id}
                    >
                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`created-date-${pres.id}`}
                          className="text-sm font-semibold  text-secondary-pink-700"
                        >
                          Created Date
                        </label>
                        <div className="mt-2">
                          <div className="relative custom-date-picker">
                            <CustomDatePicker
                              value={pres.created_date}
                              handleDate={(date: Date) =>
                                handleDate(date, 'created_date', pres.id)
                              }
                              id={pres.id}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`eRxToken-${pres.id}`}
                          className="text-sm font-semibold  text-secondary-pink-700"
                        >
                          eRx Token
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="eRxToken"
                            id={`eRxToken-${pres.id}`}
                            autoComplete="address-level1"
                            className="text-sm font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                            value={pres.eRxToken}
                            onChange={(e) => handleInput(e, pres.id)}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`doctor-${pres.id}`}
                          className="text-sm font-semibold  text-secondary-pink-700"
                        >
                          Doctor
                        </label>
                        <div className="mt-2 custom-select">
                          <Select
                            value={pres.doctor}
                            options={doctors}
                            onChange={(selectedOption) =>
                              handleSelect(
                                selectedOption as {
                                  label: string;
                                  value: number;
                                },
                                'doctor',
                                pres.id,
                              )
                            }
                            isSearchable={true}
                            components={{
                              DropdownIndicator,
                              IndicatorSeparator: () => null,
                            }}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`quantity-${pres.id}`}
                          className="text-sm font-semibold  text-secondary-pink-700"
                        >
                          Quantity <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <input
                            type="number"
                            name="quantity"
                            id={`quantity-${pres.id}`}
                            autoComplete="address-level1"
                            className={`text-sm font-regular border  rounded-md p-2 w-full
                            ${error && !pres.quantity ? 'border-red-400' : 'border-secondary-cornflowerblue-400'}
                            `}
                            value={pres.quantity}
                            onChange={(e) => handleInput(e, pres.id)}
                            placeholder="2"
                          />
                        </div>
                        {error && !pres.quantity && (
                          <small className="text-red-600">
                            Quantity is required
                          </small>
                        )}
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`repeats-${pres.id}`}
                          className="text-sm font-semibold  text-secondary-pink-700"
                        >
                          Repeats <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2">
                          <input
                            type="number"
                            name="repeats"
                            id={`repeats-${pres.id}`}
                            autoComplete="address-level1"
                            className={`text-sm font-regular border  rounded-md p-2 w-full
                            ${error && !pres.repeats ? 'border-red-400' : 'border-secondary-cornflowerblue-400'}
                            `}
                            value={pres.repeats}
                            onChange={(e) => handleInput(e, pres.id)}
                            placeholder="5"
                          />
                        </div>
                        {error && !pres.repeats && (
                          <small className="text-red-600">
                            Repeats is required
                          </small>
                        )}
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`interval-${pres.id}`}
                          className="text-sm font-semibold  text-secondary-pink-700"
                        >
                          Interval
                        </label>
                        <div className="mt-2">
                          <input
                            type="number"
                            name="interval"
                            id={`interval-${pres.id}`}
                            autoComplete="address-level1"
                            className="text-sm font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                            value={pres.interval}
                            onChange={(e) => handleInput(e, pres.id)}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`product-${pres.id}`}
                          className="text-sm font-semibold  text-secondary-pink-700"
                        >
                          Product <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-2 custom-select">
                          <Select
                            value={pres.product}
                            options={products}
                            onChange={(selectedOption) =>
                              handleSelect(
                                selectedOption as {
                                  label: string;
                                  value: number;
                                },
                                'product',
                                pres.id,
                              )
                            }
                            isSearchable={true}
                            components={{
                              DropdownIndicator,
                              IndicatorSeparator: () => null,
                            }}
                            menuPlacement="top"
                            className={
                              error && !pres.product.value ? 'select-error' : ''
                            }
                          />
                        </div>
                        {error && !pres.product.value && (
                          <small className="text-red-600">
                            Product is required
                          </small>
                        )}
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor={`dosage-${pres.id}`}
                          className="text-sm font-semibold  text-secondary-pink-700"
                        >
                          Dosage Instructions
                        </label>
                        <div className="mt-2">
                          <input
                            type="text"
                            name="dosage"
                            id={`dosage-${pres.id}`}
                            autoComplete="address-level1"
                            className="text-sm font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                            value={pres.dosage}
                            onChange={(e) => handleInput(e, pres.id)}
                          />
                        </div>
                      </div>
                    </div>
                    <p
                      className="text-primary-blue-500 text-4xl font-semibold cursor-pointer absolute top-0 right-4"
                      onClick={() => removePrescription(pres.id)}
                    >
                      {' '}
                      <span>-</span>
                    </p>
                  </div>
                ))}
            </div>

            <div className="flex items-center justify-end gap-x-6 py-8">
              <button
                type="button"
                className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 w-24 px-4 py-2 rounded"
                onClick={clearInput}
              >
                Clear
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="bg-primary-blue-500 border border-solid border-primary-blue-500 text-white px-4 py-2 w-24 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardPatient;
